import { motion } from "framer-motion";
import { CreditCard, ShieldCheck, Zap, Globe, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useNearViewport } from "@/hooks/use-near-viewport";

import telebirr from "@/assets/payments/telebirr-optimized.webp";
import awashBirr from "@/assets/payments/awash-birr-optimized.webp";
import abyssinia from "@/assets/payments/abyssinia-optimized.webp";
import zemen from "@/assets/payments/zemen-optimized.webp";
import cbeBirr from "@/assets/payments/cbebirr-optimized.webp";
import stripe from "@/assets/payments/stripe-optimized.webp";
import paypal from "@/assets/payments/paypal-optimized.webp";

const logos = [
    { src: telebirr, alt: "Telebirr" },
    { src: awashBirr, alt: "Awash BIRR" },
    { src: abyssinia, alt: "Bank of Abyssinia" },
    { src: zemen, alt: "Zemen Bank" },
    { src: cbeBirr, alt: "CBE Birr" },
    { src: stripe, alt: "Stripe" },
    { src: paypal, alt: "PayPal" },
];

const stats = [
    { value: "7+", label: "Payment Methods" },
    { value: "100%", label: "Secure & Encrypted" },
    { value: "< 3s", label: "Receipt Delivery" },
];

const PaymentSection = () => {
    const showDesktopVisualization = useMediaQuery("(min-width: 1024px)");
    const { ref: sectionRef, isNearViewport } = useNearViewport<HTMLElement>();

    return (
    <section ref={sectionRef} className="relative py-28 overflow-hidden">
        {/* Deep gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-white dark:from-slate-900 dark:via-slate-800 dark:to-slate-900" />

        {/* Mesh grid overlay */}
        <div
            className="absolute inset-0 opacity-[0.04] dark:opacity-[0.03]"
            style={{
                backgroundImage: `linear-gradient(rgba(0,0,0,0.4) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,0,0,0.4) 1px, transparent 1px)`,
                backgroundSize: "40px 40px",
            }}
        />

        {/* Glow blobs */}
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-primary/8 dark:bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-0 right-0 w-72 h-72 bg-violet-400/8 dark:bg-violet-600/15 rounded-full blur-[100px] pointer-events-none" />

        <div className="container mx-auto px-4 lg:px-8 max-w-7xl relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">

                {/* ── Left: Copy ───────────────────────────────────────────────────── */}
                <motion.div
                >
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-6 border border-primary/20">
                        <CreditCard className="w-4 h-4 text-primary" /> Fee Management
                    </span>

                    <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-slate-900 dark:text-white mb-6 leading-tight tracking-tight">
                        Pay School Fees{" "}
                        <span className="text-primary">
                            Your Way
                        </span>
                    </h2>

                    <p className="text-lg text-slate-500 dark:text-slate-400 leading-relaxed mb-8 max-w-lg">
                        Pay school fees conveniently through the app and receive instant email receipts.
                        Many online payment options available — popular choices in Ethiopia and worldwide.
                    </p>

                    {/* Feature pills */}
                    <div className="flex flex-col gap-3 mb-10">
                        {[
                            { icon: ShieldCheck, text: "Bank-grade security", color: "text-emerald-500" },
                            { icon: Zap, text: "Instant receipts delivered to email", color: "text-yellow-500" },
                            { icon: Globe, text: "Ethiopian & international gateways", color: "text-sky-500" },
                        ].map((f) => (
                            <div key={f.text} className="flex items-center gap-3 dark:">
                                <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/10 flex items-center justify-center flex-shrink-0">
                                    <f.icon className={`w-4 h-4 ${f.color}`} />
                                </div>
                                <span className="text-sm text-slate-600 dark:text-slate-300">{f.text}</span>
                            </div>
                        ))}
                    </div>

                    {/* Mini stats */}
                    <div className="flex gap-8 mb-10">
                        {stats.map((s) => (
                            <div key={s.label}>
                                <p className="text-2xl font-extrabold text-slate-900 dark:text-white">{s.value}</p>
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{s.label}</p>
                            </div>
                        ))}
                    </div>

                    {/* Mobile payment-method grid */}
                    <div
                        aria-label="Supported payment methods"
                        className="mb-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:hidden"
                    >
                        {logos.map((logo) => (
                            <div
                                key={logo.alt}
                                className="flex h-20 min-w-0 items-center justify-center rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900"
                            >
                                <img
                                    src={logo.src}
                                    alt={logo.alt}
                                    width="400"
                                    height="200"
                                    className="h-full w-full object-contain"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                        ))}
                    </div>

                    <Link to="/features">
                        <button className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors shadow-lg shadow-primary/25">
                            Explore Fee Features <ArrowRight className="w-4 h-4" />
                        </button>
                    </Link>
                </motion.div>

                {/* ── Desktop: orbital circle ──────────────────────────────────── */}
                <motion.div
                    className="relative hidden items-center justify-center lg:flex"
                    style={{ height: 480 }}
                >
                    {showDesktopVisualization && isNearViewport && <>
                    {/* Orbit ring */}
                    <div
                        className="absolute rounded-full border-2 border-dashed border-primary/20 dark:border-primary/30"
                        style={{ width: 440, height: 440 }}
                    />

                    {/* Slow spin wrapper for logos */}
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
                        className="absolute"
                        style={{ width: 440, height: 440 }}
                    >
                        {logos.map((logo, i) => {
                            const angle = (i / logos.length) * 360;
                            const radius = 220; // half of 440
                            const rad = (angle * Math.PI) / 180;
                            const x = radius + radius * Math.sin(rad) - 40; // center - half card size
                            const y = radius - radius * Math.cos(rad) - 40;
                            return (
                                <motion.div
                                    key={i}
                                    whileHover={{ scale: 1.15 }}
                                    className="absolute w-[80px] h-[80px] rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-md flex items-center justify-center p-2 cursor-default"
                                    style={{ left: x, top: y }}
                                >
                                    {/* Counter-rotate so logos stay upright */}
                                    <motion.img
                                        animate={{ rotate: -360 }}
                                        transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
                                        src={logo.src}
                                        alt={logo.alt}
                                        width="200"
                                        height="200"
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-full object-contain"
                                        draggable={false}
                                    />
                                </motion.div>
                            );
                        })}
                    </motion.div>

                    {/* Center shield — premium */}
                    <div className="relative z-10 flex flex-col items-center gap-2">
                        {/* Outer pulse ring */}
                        <div className="relative flex items-center justify-center">
                            <motion.div
                                animate={{ scale: [1, 1.18, 1], opacity: [0.4, 0, 0.4] }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                className="absolute w-36 h-36 rounded-full bg-primary/20"
                            />
                            <motion.div
                                animate={{ scale: [1, 1.35, 1], opacity: [0.2, 0, 0.2] }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                                className="absolute w-36 h-36 rounded-full bg-primary/10"
                            />

                            {/* Main circle */}
                            <div className="relative w-28 h-28 rounded-full bg-gradient-to-br from-primary via-primary to-violet-500 shadow-2xl shadow-primary/40 flex items-center justify-center">
                                {/* Glass inner ring */}
                                <div className="absolute inset-2 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm" />
                                {/* Top shine */}
                                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-5 bg-white/20 rounded-full blur-sm" />
                                {/* Shield icon */}
                                <ShieldCheck className="relative z-10 w-12 h-12 text-white drop-shadow-sm" strokeWidth={1.5} />
                            </div>
                        </div>

                        {/* Label below */}
                        <span className="text-xs font-bold text-primary bg-primary/10 border border-primary/20 px-3 py-1 rounded-full tracking-wider uppercase">
                            Secured
                        </span>
                    </div>
                    </>}
                </motion.div>

            </div>
        </div>
    </section>
    );
};

export default PaymentSection;
